import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { ifNonEmpty } from '../../base/directives';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { formatHtml5Duration, formatTime } from '../../utils/time';
import { timeElementStyles } from './styles';
export const TIME_ELEMENT_TAG_NAME = 'vds-time';
/**
 * Formats and displays a length of time given in `seconds`.
 *
 * @tagname vds-time
 * @csspart root - The component's root element (`<time>`).
 * @example
 * ```html
 * <vds-time seconds="50"></vds-time>
 * ```
 * @example
 * ```html
 * <vds-time
 *   label="Current time"
 *   seconds="3650"
 *   pad-hours
 *   always-show-hours
 * ></vds-time>
 * ```
 * @example
 * ```css
 * vds-time::part(root) {
 *   font-size: 16px;
 * }
 * ```
 */
export class TimeElement extends LitElement {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        /**
         * The length of time in seconds.
         */
        this.seconds = 0;
        /**
         * Whether the time should always show the hours unit, even if the time is less than
         * 1 hour.
         *
         * @example `20:30` -> `0:20:35`
         */
        this.alwaysShowHours = false;
        /**
         * Whether the hours unit should be padded with zeroes to a length of 2.
         *
         * @example `1:20:03` -> `01:20:03`
         */
        this.padHours = false;
        // -------------------------------------------------------------------------------------------
        // Render
        // -------------------------------------------------------------------------------------------
        this._rootRef = createRef();
    }
    static get styles() {
        return [timeElementStyles];
    }
    static get parts() {
        return ['root', 'time'];
    }
    /**
     * The component's root element.
     */
    get rootElement() {
        return this._rootRef.value;
    }
    render() {
        return html `
      <time
        id="root"
        part="root time"
        aria-label=${ifNonEmpty(this.label)}
        datetime=${this._getFormattedDuration()}
        ${ref(this._rootRef)}
      >
        ${this._renderRootChildren()}
      </time>
    `;
    }
    _renderRootChildren() {
        return html `${this._getFormattedTime()}`;
    }
    /**
     * @returns The seconds formatted into human readable form.
     */
    _getFormattedTime() {
        return formatTime(this.seconds, this.padHours, this.alwaysShowHours);
    }
    /**
     * @returns A valid HTML5 duration.
     * @see https://www.w3.org/TR/2014/REC-html5-20141028/infrastructure.html#valid-duration-string
     */
    _getFormattedDuration() {
        return formatHtml5Duration(this.seconds);
    }
}
__decorate([
    property()
], TimeElement.prototype, "label", void 0);
__decorate([
    property({ type: Number })
], TimeElement.prototype, "seconds", void 0);
__decorate([
    property({ attribute: 'always-show-hours', type: Boolean })
], TimeElement.prototype, "alwaysShowHours", void 0);
__decorate([
    property({ attribute: 'pad-hours', type: Boolean })
], TimeElement.prototype, "padHours", void 0);
